// MobileForgotPassword — tela de RECUPERAÇÃO DE SENHA mobile (parede A1).
//
// Fluxo em 2 passos (formato CÓDIGO, não link — o app é encapsulado/webview e
// ainda não tem deep-link confiável; o usuário recebe um código de 6 chars por
// email e digita aqui):
//   Passo 1 ('request'): informa o email → chama /password/forgot. O backend
//     SEMPRE responde 200 genérico (anti-enumeração), então mostramos a mesma
//     confirmação exista ou não a conta, e avançamos pro passo 2.
//   Passo 2 ('confirm'): digita o código recebido + a nova senha → chama
//     /password/reset. Em sucesso, a senha já foi trocada e as sessões antigas
//     revogadas no backend; mandamos o usuário voltar pro login.
//
// Touch-first, sem foco de controle remoto. Validação local (email/senha) antes
// de bater no servidor, igual ao MobileSignup.

import { useTranslation } from '@app-iptv/i18n'
import { useState } from 'react'

import { confirmPasswordReset, requestPasswordReset } from '../auth/account-auth'

import { resetErrorKey } from './auth-messages'
import styles from './MobileAuth.module.css'
import { MobileBrandLockup } from './MobileBrandLockup'

interface MobileForgotPasswordProps {
  /** Voltar pra tela de login. */
  onGoToLogin: () => void
}

const MIN_PASSWORD = 8 // bate com SwipePasswordResetBody (z.string().min(8))

function looksLikeEmail(value: string): boolean {
  return /.+@.+\..+/.test(value.trim())
}

type Step = 'request' | 'confirm' | 'done'

export function MobileForgotPassword({ onGoToLogin }: MobileForgotPasswordProps): JSX.Element {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<'email' | 'code' | 'password' | null>(null)

  const handleRequest = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setErrorKey(null)
    setFieldError(null)

    if (!looksLikeEmail(email)) {
      setFieldError('email')
      setErrorKey('mobileAuth.error.invalidEmail')
      return
    }

    setBusy(true)
    try {
      const result = await requestPasswordReset(email)
      // Sucesso = backend aceitou (genérico). Erros transitórios mostram a copy
      // de rede/servidor; em sucesso avançamos pro passo de código.
      if (result.ok) setStep('confirm')
      else setErrorKey(resetErrorKey(result.error))
    } finally {
      setBusy(false)
    }
  }

  const handleConfirm = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setErrorKey(null)
    setFieldError(null)

    if (code.trim().length === 0) {
      setFieldError('code')
      setErrorKey('mobileAuth.error.resetCodeRequired')
      return
    }
    if (password.length < MIN_PASSWORD) {
      setFieldError('password')
      setErrorKey('mobileAuth.error.passwordTooShort')
      return
    }

    setBusy(true)
    try {
      const result = await confirmPasswordReset(code, password)
      if (result.ok) setStep('done')
      else setErrorKey(resetErrorKey(result.error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.brandLogo}>
            <MobileBrandLockup height={48} />
          </span>
          <h1 className={styles.title}>{t('mobileAuth.forgot.title')}</h1>
          <p className={styles.subtitle}>
            {step === 'request'
              ? t('mobileAuth.forgot.subtitle')
              : step === 'confirm'
                ? t('mobileAuth.forgot.confirmSubtitle')
                : t('mobileAuth.forgot.doneSubtitle')}
          </p>
        </header>

        {step === 'request' && (
          <form className={styles.form} onSubmit={(e) => void handleRequest(e)} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="mobile-forgot-email">
                {t('mobileAuth.emailLabel')}
              </label>
              <input
                id="mobile-forgot-email"
                className={`${styles.input} ${fieldError === 'email' ? styles.inputError : ''}`}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('mobileAuth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </div>

            {errorKey && (
              <p className={styles.error} role="alert">
                {t(errorKey)}
              </p>
            )}

            <button type="submit" className={`${styles.button} ${styles.primary}`} disabled={busy}>
              {busy ? (
                <span className={styles.spinner} aria-hidden="true" />
              ) : (
                t('mobileAuth.forgot.submit')
              )}
            </button>
          </form>
        )}

        {step === 'confirm' && (
          <form className={styles.form} onSubmit={(e) => void handleConfirm(e)} noValidate>
            <p className={styles.subtitle} role="status">
              {t('mobileAuth.forgot.sentHint')}
            </p>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="mobile-forgot-code">
                {t('mobileAuth.forgot.codeLabel')}
              </label>
              <input
                id="mobile-forgot-code"
                className={`${styles.input} ${fieldError === 'code' ? styles.inputError : ''}`}
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('mobileAuth.forgot.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="mobile-forgot-password">
                {t('mobileAuth.forgot.newPasswordLabel')}
              </label>
              <input
                id="mobile-forgot-password"
                className={`${styles.input} ${fieldError === 'password' ? styles.inputError : ''}`}
                type="password"
                autoComplete="new-password"
                placeholder={t('mobileAuth.forgot.newPasswordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </div>

            {errorKey && (
              <p className={styles.error} role="alert">
                {t(errorKey)}
              </p>
            )}

            <button type="submit" className={`${styles.button} ${styles.primary}`} disabled={busy}>
              {busy ? (
                <span className={styles.spinner} aria-hidden="true" />
              ) : (
                t('mobileAuth.forgot.confirmSubmit')
              )}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className={styles.form}>
            <p className={styles.success} role="status">
              {t('mobileAuth.forgot.doneMessage')}
            </p>
            <button
              type="button"
              className={`${styles.button} ${styles.primary}`}
              onClick={onGoToLogin}
            >
              {t('mobileAuth.forgot.backToLogin')}
            </button>
          </div>
        )}

        <div className={styles.links}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={onGoToLogin}
            disabled={busy}
          >
            {t('mobileAuth.forgot.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  )
}
